import { marked } from 'marked';

import { HelpArticleMeta, HelpAudience, HelpFeature, HelpSearchHit, HelpSection } from '../models';

/**
 * Opens a block only some readers see: `::: permission manage_settings` for readers whose role holds
 * the permission, `::: feature kmd_menu_links` for readers where the feature is on.
 */
export const BLOCK_START = /^:::\s*(permission|feature)\s+(\S+)\s*$/;
/** Closes it */
export const BLOCK_END = /^:::\s*$/;

/** A search needs at least this many characters before it finds anything. */
export const MIN_QUERY_LENGTH = 2;

/**
 * Removes the `::: permission <key>` and `::: feature <key>` … `:::` blocks the reader may not see
 * and the marker lines of the ones they may. Blocks do not nest; an unclosed block runs to the end
 * of the file.
 * @param markdown The article as written
 * @param canI Answers whether the reader holds a permission key
 * @param hasFeature Answers whether a feature is on for the reader; a feature nobody asks about is off
 * @returns The markdown to render for this reader
 */
export function stripPermissionBlocks(
  markdown: string,
  canI: (permission: string) => boolean,
  hasFeature: (feature: HelpFeature) => boolean = () => false,
): string {
  const kept: string[] = [];
  let hiding = false;

  for (const line of markdown.split('\n')) {
    const start = line.match(BLOCK_START);

    if (start) {
      hiding = !(start[1] === 'feature' ? hasFeature(start[2] as HelpFeature) : canI(start[2]));
      continue;
    }

    if (BLOCK_END.test(line)) {
      hiding = false;
      continue;
    }

    if (!hiding) {
      kept.push(line);
    }
  }

  return kept.join('\n');
}

/** The permission keys an article's `::: permission` blocks ask about, once each. */
export function permissionsInMarkdown(markdown: string): string[] {
  const keys = new Set<string>();

  for (const line of markdown.split('\n')) {
    const start = line.match(BLOCK_START);
    if (start?.[1] === 'permission') keys.add(start[2]);
  }

  return [...keys];
}

/** The features an article's `::: feature` blocks ask about, once each, as written. */
export function featuresInMarkdown(markdown: string): string[] {
  const keys = new Set<string>();

  for (const line of markdown.split('\n')) {
    const start = line.match(BLOCK_START);
    if (start?.[1] === 'feature') keys.add(start[2]);
  }

  return [...keys];
}

/**
 * Renders help markdown to HTML. The result goes through Angular's `[innerHTML]` sanitizer, so
 * only the plain HTML that marked produces survives — the articles are our own files, but a
 * sanitized page needs no trust decision at all.
 */
export function renderHelpMarkdown(markdown: string): string {
  return marked.parse(markdown, { async: false, gfm: true });
}

/** The words of the article, for searching — tags dropped, whitespace folded. */
export function toSearchText(markdown: string): string {
  return (
    renderHelpMarkdown(markdown)
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      // Last, so a literal "&lt;" in the article (marked writes it as "&amp;lt;") is not decoded twice.
      .replace(/&amp;/g, '&')
      .replace(/\s+/g, ' ')
      // The tag boundaries left a space before punctuation: "<strong>Afbooket</strong>," → "Afbooket ,".
      .replace(/ ([.,;:!?])/g, '$1')
      .trim()
  );
}

/**
 * The sections and articles a reader may see: a section or article without a permission is open
 * to every signed-in user, the rest need a yes from `canI`; one written for the other audience is
 * left out, and so is one tied to a feature that is off for the reader. Sections left without
 * articles drop.
 * @param audience The reader's side of the app; `null` while unknown, which hides everything
 * written for one side only
 * @param hasFeature Answers whether a feature is on for the reader; features are off by default
 */
export function visibleSections(
  manifest: readonly HelpSection[],
  canI: (permission: string) => boolean,
  audience: HelpAudience | null,
  hasFeature: (feature: HelpFeature) => boolean = () => false,
): HelpSection[] {
  const allowed = (item: { permission?: string; audience?: HelpAudience; feature?: HelpFeature }) =>
    (!item.permission || canI(item.permission)) &&
    (!item.feature || hasFeature(item.feature)) &&
    (!item.audience || item.audience === audience);

  return manifest
    .filter(allowed)
    .map((section) => ({ ...section, articles: section.articles.filter(allowed) }))
    .filter((section) => section.articles.length > 0);
}

/** Every permission key the manifest mentions, once each — what has to be asked before filtering. */
export function permissionsInManifest(manifest: readonly HelpSection[]): string[] {
  const keys = new Set<string>();

  for (const section of manifest) {
    if (section.permission) keys.add(section.permission);
    for (const article of section.articles) {
      if (article.permission) keys.add(article.permission);
    }
  }

  return [...keys];
}

/** Where an article's markdown is served from, relative to the app's base href. */
export function articleUrl(section: HelpSection, article: HelpArticleMeta): string {
  return `assets/help/${section.slug}/${article.slug}.md`;
}

export function articleKey(sectionSlug: string, articleSlug: string): string {
  return `${sectionSlug}/${articleSlug}`;
}

const EXCERPT_RADIUS = 70;

/**
 * Case-insensitive substring search over title, summary and body. A hit's excerpt is the text
 * around the first match in the body — or the summary when only the title or summary matched.
 * @param sections The sections the reader may see
 * @param texts Search text per article key (see {@link articleKey}); articles not loaded yet are
 * searched on title and summary only
 * @param query What the reader typed; shorter than {@link MIN_QUERY_LENGTH} finds nothing
 */
export function searchArticles(
  sections: readonly HelpSection[],
  texts: Readonly<Record<string, string>>,
  query: string,
): HelpSearchHit[] {
  const needle = query.trim().toLowerCase();

  if (needle.length < MIN_QUERY_LENGTH) {
    return [];
  }

  const hits: HelpSearchHit[] = [];

  for (const section of sections) {
    for (const article of section.articles) {
      const text = texts[articleKey(section.slug, article.slug)] ?? '';
      const at = text.toLowerCase().indexOf(needle);

      if (at >= 0) {
        hits.push({ section, article, excerpt: excerptAround(text, at, needle.length) });
      } else if (`${article.title} ${article.summary}`.toLowerCase().includes(needle)) {
        hits.push({ section, article, excerpt: article.summary });
      }
    }
  }

  return hits;
}

function excerptAround(text: string, at: number, length: number): string {
  const from = Math.max(0, at - EXCERPT_RADIUS);
  const to = Math.min(text.length, at + length + EXCERPT_RADIUS);
  const prefix = from > 0 ? '…' : '';
  const suffix = to < text.length ? '…' : '';

  return `${prefix}${text.slice(from, to).trim()}${suffix}`;
}
